import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { createLemonSqueezyCheckout } from '../../api';
import messageQueue from '../../utils/messageQueue';
import MessageConstant from '../../utils/Constant/MessageConstant';
import UserHeaderBar from '../../components/userHeaderBar/userHeaderBar';
import styles from '../../publicAsset/checkoutPage.module.scss';

const CheckoutPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [packageData, setPackageData] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);

        // Get package data from navigation state
        if (location.state?.package) {
            setPackageData(location.state.package);
        } else {
            // Redirect back if no package data
            messageQueue.warning('NO_PACKAGE_SELECTED', MessageConstant.NO_PACKAGE_SELECTED);
            navigate('/user-package');
        }
    }, [location, navigate]);

    const formatPrice = (price) => {
        if (price === null || price === undefined) return '0';
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(price);
    };

    const handleCheckout = async () => {
        if (!packageData) {
            messageQueue.error('PACKAGE_INFO_MISSING', MessageConstant.PACKAGE_INFO_MISSING);
            return;
        }

        setIsProcessing(true);

        try {
            const lemonSqueezyVariantId = packageData.lemon_squeezy_variant_id || packageData.variant_id;

            if (!lemonSqueezyVariantId) {
                messageQueue.error('PACKAGE_NOT_CONFIGURED', MessageConstant.PACKAGE_NOT_CONFIGURED);
                setIsProcessing(false);
                return;
            }

            const successUrl = `${window.location.origin}/user-setting?payment=success&packageId=${encodeURIComponent(packageData.id)}&packageName=${encodeURIComponent(packageData.name || '')}`;

            const response = await createLemonSqueezyCheckout({
                packageId: packageData.id,
                variantId: String(lemonSqueezyVariantId),
                successUrl,
            });

            const checkoutUrl = response?.data?.checkoutUrl;
            if (!checkoutUrl) {
                throw new Error('Missing checkout URL from server.');
            }

            window.location.assign(checkoutUrl);
        } catch (error) {
            console.error('Lemon Squeezy checkout error:', error);
            messageQueue.error('CHECKOUT_FAILED', MessageConstant.CHECKOUT_FAILED);
            setIsProcessing(false);
        }
    };

    if (!packageData) {
        return (
            <div className={styles.checkoutPage}>
                <UserHeaderBar />
                <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                    <p>Loading checkout...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Checkout - {packageData.name} | Prepal</title>
                <meta name="description" content="Complete your purchase and unlock your SAT preparation journey" />
            </Helmet>

            <div className={styles.checkoutPage}>

                <div className={styles.checkoutContainer}>
                    <div className={styles.checkoutWrapper}>
                        {/* Header */}
                        <div className={styles.checkoutHeader}>
                            <h1 className={styles.title}>Complete Your Purchase</h1>
                            <p className={styles.subtitle}>You're one step away from unlocking your full potential</p>
                        </div>

                        <div className={styles.checkoutContent}>
                            {/* Order Summary Card */}
                            <div className={styles.summaryCard}>
                                <div className={styles.cardHeader}>
                                    <h2 className={styles.cardTitle}>Order Summary</h2>
                                </div>

                                <div className={styles.packageInfo}>
                                    <div className={styles.packageBadge}>
                                        <svg width="48" height="48" viewBox="0 0 20 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" stroke="#220ec9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <div className={styles.packageDetails}>
                                        <h3 className={styles.packageName}>{packageData.name}</h3>
                                        {packageData.description && (
                                            <p className={styles.packageDescription}>{packageData.description}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Features */}
                                {packageData.features && packageData.features.length > 0 && (
                                    <div className={styles.featuresSection}>
                                        <h4 className={styles.featuresTitle}>What's Included:</h4>
                                        <ul className={styles.featuresList}>
                                            {packageData.features.map((feature, index) => (
                                                <li key={index} className={styles.featureItem}>
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <circle cx="12" cy="12" r="10" fill="#e8f5e9" />
                                                        <path d="M9 12l2 2 4-4" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                    <span>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Price Breakdown */}
                                <div className={styles.priceBreakdown}>
                                    <div className={styles.priceRow}>
                                        <span className={styles.label}>Duration:</span>
                                        <span className={styles.value}>
                                            {packageData.duration_value} {packageData.duration_type}{packageData.duration_value > 1 ? 's' : ''}
                                        </span>
                                    </div>

                                    {packageData.discount_percent > 0 && (
                                        <>
                                            <div className={styles.priceRow}>
                                                <span className={styles.label}>Original Price:</span>
                                                <span className={styles.value}>
                                                    {formatPrice(packageData.price_vnd)} VND{packageData.price_usd !== undefined ? ` · $${packageData.price_usd} USD` : ''}
                                                </span>
                                            </div>
                                            <div className={`${styles.priceRow} ${styles.discount}`}>
                                                <span className={styles.label}>Discount:</span>
                                                <span className={styles.value}>
                                                    -{packageData.discount_percent}%
                                                </span>
                                            </div>
                                        </>
                                    )}

                                    <div className={styles.divider}></div>

                                    <div className={`${styles.priceRow} ${styles.total}`}>
                                        <span className={styles.label}>Total Amount:</span>
                                        <span className={styles.value}>
                                            {formatPrice(packageData.price_vnd)} VND{packageData.price_usd !== undefined ? ` · $${packageData.price_usd} USD` : ''}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Payment Card */}
                            <div className={styles.paymentCard}>
                                <div className={styles.cardHeader}>
                                    <h2 className={styles.cardTitle}>Payment Method</h2>
                                </div>

                                <div className={styles.paymentContent}>
                                    {/* Lemon Squeezy Payment Info */}
                                    <div className={styles.paymentProvider}>
                                        <div className={styles.providerLogo}>
                                            <svg width="220" height="40" viewBox="0 0 220 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Lemon Squeezy">
                                                <text x="10" y="28" fill="#3b3f5c" fontSize="18" fontWeight="700" fontFamily="Arial">Lemon Squeezy</text>
                                            </svg>
                                        </div>
                                        <p className={styles.providerDescription}>
                                            Secure checkout powered by Lemon Squeezy with support for major cards and international payments.
                                        </p>
                                    </div>

                                    {/* Payment Methods */}
                                    <div className={styles.acceptedMethods}>
                                        <span className={styles.methodsLabel}>Accepted Payment Methods:</span>
                                        <div className={styles.methodsIcons}>
                                            <div className={styles.methodIcon}>💳 Credit Card</div>
                                            <div className={styles.methodIcon}>🅿️ PayPal</div>
                                            <div className={styles.methodIcon}>🏦 Bank Transfer</div>
                                        </div>
                                    </div>

                                    {/* Checkout Button */}
                                    <button
                                        className={styles.checkoutButton}
                                        onClick={handleCheckout}
                                        disabled={isProcessing}
                                    >
                                        {isProcessing ? (
                                            <>
                                                <div className={styles.buttonSpinner}></div>
                                                <span>Processing...</span>
                                            </>
                                        ) : (
                                            <>
                                                <svg width="20" height="20" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                <span>Proceed to Secure Checkout</span>
                                            </>
                                        )}
                                    </button>

                                    {/* Security Notice */}
                                    <div className={styles.securityNotice}>
                                        <svg width="16" height="16" viewBox="0 0 24 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M9 12l2 2 4-4" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <span>256-bit SSL encrypted secure payment</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Info Section */}
                        <div className={styles.infoSection}>
                            <div className={styles.infoCard}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="10" stroke="#7d7fff" strokeWidth="2" />
                                    <path d="M12 8v4M12 16h.01" stroke="#7d7fff" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                                <div>
                                    <h4>Money-Back Guarantee</h4>
                                    <p>30-day money-back guarantee if you're not satisfied</p>
                                </div>
                            </div>
                            <div className={styles.infoCard}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="10" stroke="#7d7fff" strokeWidth="2" />
                                    <path d="M12 6v6l4 2" stroke="#7d7fff" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                                <div>
                                    <h4>Instant Activation</h4>
                                    <p>Your subscription activates immediately after payment</p>
                                </div>
                            </div>
                            <div className={styles.infoCard}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="#7d7fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <div>
                                    <h4>24/7 Support</h4>
                                    <p>Get help anytime from our dedicated support team</p>
                                </div>
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className={styles.checkoutFooter}>
                            <button
                                className={styles.backButton}
                                onClick={() => navigate('/user-package')}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span>Back to Plans</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CheckoutPage;
